import { useEffect, useRef, useState } from "react";
import type { CoachVisibleProfile } from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";

// <useProfileNames> — GET /api/profiles/:sub, batched over however many subs
// the caller hands it, with a per-mount cache so a name resolved once is
// never re-fetched. Extracted (PE-48 review pass) out of two hand-rolled
// copies of the SAME effect that had already drifted apart:
// ComposerAssigneesBand.tsx (batch + cache) and LibraryCoachBadge.tsx
// (single sub, no cache). See next/consolidation.md for the census.
//
// Fails soft PER SUB: a 404/network error on one profile leaves that sub
// unresolved (never in the returned map) rather than failing the whole
// batch or ever surfacing the raw sub as a name.
//
// ⛔ NOT useCoachDashboard.ts's fan-out. That hook resolves a profile's name
// as one leg of a 5-way Promise.all per athlete (programs/session-results/
// exercises/targets ride along in the SAME fan-out); this hook does ONLY
// the name, batched over N subs, with none of those other reads. It stays a
// third, deliberately different shape — see next/consolidation.md.
export function useProfileNames(
  apiFetch: ProgramApiFetch,
  subs: ReadonlyArray<string>,
): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>({});
  // Per-mount cache: only NEW subs (never seen this mount) trigger a read.
  const resolvedRef = useRef<Set<string>>(new Set());
  const subsKey = [...subs].sort().join(",");

  // PE-48 review fix #2 — this used to tie "alive" to THIS effect run: the
  // cleanup fired on every deps change (not just real unmount), and a batch
  // in flight when `subsKey` changed (e.g. useCoachStatus resolving right
  // after mount, changing the roster) got its `resolvedRef` entries stamped
  // BEFORE the await, then its result thrown away by `if (!alive) return`
  // after the await. Nobody ever un-stamped `resolvedRef`, so those subs
  // were "resolved" forever with no name ever landing in `names` — the
  // caller's own name, most often, since it resolves in the very first
  // batch on mount.
  //
  // Fix chosen (the review named two): keep "alive" scoped to a real
  // UNMOUNT, not to a deps change — a fetch started before the sub list
  // changed is still answering a question this hook still cares about, so
  // its result should still land. (The alternative — stamp `resolvedRef`
  // only on success — would still need a separate real-unmount tracker to
  // avoid a setState-after-unmount warning, and would additionally retry
  // every already-failed sub on each subsKey change; this is the more
  // honest of the two.)
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    const toFetch = subs.filter((s) => !resolvedRef.current.has(s));
    if (toFetch.length === 0) return;
    for (const s of toFetch) resolvedRef.current.add(s);
    (async () => {
      const entries = await Promise.all(
        toFetch.map(async (sub) => {
          const profile = await apiFetch<CoachVisibleProfile>(
            `/api/profiles/${encodeURIComponent(sub)}`,
          ).catch(() => null);
          return [sub, profile?.name] as const;
        }),
      );
      if (!mountedRef.current) return;
      setNames((prev) => {
        const next = { ...prev };
        for (const [sub, name] of entries) if (name) next[sub] = name;
        return next;
      });
    })();
    // `apiFetch` is a stable per-host seam; `subsKey` is the real input —
    // same rationale as useCoachDashboard's rosterKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsKey]);

  return names;
}
